// eslint-disable-next-line no-restricted-imports
import { type Image3DType, useBase3DImage } from '@capsicum/common';
import { type PropsWithChildren, useState } from 'react';
import { type CSSProperties, styled } from 'styled-components';

import { Image, SkeletonItem } from '../../atoms';

export type Image3DProps = PropsWithChildren<{
	childBeforeImage?: boolean;
	className?: string;
	image: Image3DType;
	imageStyle?: CSSProperties;
	testID: string;
	title?: string;
	withoutTitleContainer?: boolean;
}>;

/**
 * Image3D component.
 * @param {string} props.testID - TestID
 * @param {Image3DType} props.image - Image key
 * @param {string} props.title - The title to display in the Image3D component
 * @param {boolean} props.withoutTitleContainer - If set, hide default title container,
 * so space between title and image will be removed
 *
 * @returns {JSX.Element} The Image3D component
 */
export const Image3D = ({
	childBeforeImage,
	children,
	className,
	image,
	imageStyle,
	testID,
	title,
	withoutTitleContainer
}: Image3DProps) => {
	const { height, src, width } = useBase3DImage(image);

	const [loading, setLoading] = useState(true);

	return (
		<S.Container className={className} data-testid={testID}>
			{loading && (
				<SkeletonItem
					height={height}
					style={{ left: '50%', position: 'absolute', top: '50%', transform: 'translate(-50%, -50%)' }}
					width={width}
				/>
			)}
			{!withoutTitleContainer && <S.Title>{title}</S.Title>}
			{childBeforeImage && children}
			<S.StyledImage
				$loading={loading}
				alt="3D image"
				height={height}
				src={src}
				style={imageStyle}
				width={width}
				onLoad={() => setLoading(false)}
			/>
			{!childBeforeImage && children}
		</S.Container>
	);
};

namespace S {
	export const Container = styled.div`
		position: relative;
		display: flex;
		flex-direction: column;
		align-items: center;
	`;

	export const Title = styled.label`
		display: block;
		text-align: center;
		${({ theme }) => theme.fonts.headings.title5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const StyledImage = styled(Image)<{ $loading: boolean }>`
		align-self: center;
		opacity: ${({ $loading }) => ($loading ? 0 : 1)};
	`;
}
